import { Plus, Upload, X } from "lucide-react";
import type React from "react";
import { OPTION_LISTS } from "../../config/fieldConfig/optionLists";
import { useFieldResolver } from "../../hooks/useFieldResolver";
import { cn } from "../../lib/utils";
import type {
	KYCDirector,
	KYCFormData,
	KYCOwnershipTier,
	KYCUltimateBeneficialOwner,
} from "../../types";
import { CANADIAN_PROVINCES, US_STATES } from "../../types";
import { FileUploadField } from "../form/FileUploadField";
import { OwnershipTierCard } from "../form/OwnershipTierCard";
import { DatePicker } from "../ui/date-picker";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";

interface Step3Props {
	formData: KYCFormData;
	updateFormData: (updates: Partial<KYCFormData>) => void;
}

function tierSummary(tier: KYCOwnershipTier): string {
	if (tier.ownerType === "Individual" && tier.fullName) {
		return `${tier.fullName} — ${tier.ownershipPercentage ?? 0}%`;
	}
	if (tier.ownerType === "Corporation" && tier.companyName) {
		return `${tier.companyName} — ${tier.ownershipPercentage ?? 0}%`;
	}
	return `${tier.ownershipPercentage ?? 0}%`;
}

const Step3Ownership: React.FC<Step3Props> = ({ formData, updateFormData }) => {
	const { thresholds } = useFieldResolver(
		{
			incorporationCountry: formData.countryOfIncorporation,
			entityType: formData.entityType,
			flowType: "KYB",
		},
		formData as unknown as Record<string, unknown>,
	);
	const addTier = () => {
		const newTier: KYCOwnershipTier = {
			tierNumber: formData.ownershipTiers.length + 1,
			ownerType: "Individual",
			ownershipPercentage: 0,
		};
		updateFormData({
			ownershipTiers: [...formData.ownershipTiers, newTier],
		});
	};

	const removeTier = (index: number) => {
		const updated = formData.ownershipTiers.filter((_, i) => i !== index);
		// Renumber tiers dynamically
		const renumbered = updated.map((tier, idx) => ({
			...tier,
			tierNumber: idx + 1,
		}));
		updateFormData({ ownershipTiers: renumbered });
	};

	const updateTier = (index: number, updates: Partial<KYCOwnershipTier>) => {
		const updated = [...formData.ownershipTiers];
		updated[index] = { ...updated[index], ...updates };
		updateFormData({ ownershipTiers: updated });
	};

	const handleConsentFormDownload = (
		formType: "georgia" | "nevada" | "canada-federal" | "canada-provincial",
	) => {
		const formPaths: Record<string, string> = {
			georgia: "/forms/Georgia Statewide Criminal History Consent Form.pdf",
			nevada: "/forms/Nevada Statewide Release Form.pdf",
			"canada-federal":
				"/forms/Canada Accurate Background - Informed consent form.pdf",
			"canada-provincial":
				"/forms/Canada Accurate Background - Credit Check.pdf",
		};

		const link = document.createElement("a");
		link.href = formPaths[formType];
		link.download = formPaths[formType].split("/").pop() || "consent-form.pdf";
		link.target = "_blank";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	// UBO management functions
	const addUBO = (tierIndex: number) => {
		const tier = formData.ownershipTiers[tierIndex];
		const newUBO: KYCUltimateBeneficialOwner = {
			ownerType: "Individual",
			ownershipPercentage: 0,
		};
		const updatedUBOs = [...(tier.ultimateBeneficialOwners || []), newUBO];
		updateTier(tierIndex, { ultimateBeneficialOwners: updatedUBOs });
	};

	const removeUBO = (tierIndex: number, uboIndex: number) => {
		const tier = formData.ownershipTiers[tierIndex];
		const updatedUBOs = (tier.ultimateBeneficialOwners || []).filter(
			(_, i) => i !== uboIndex,
		);
		updateTier(tierIndex, { ultimateBeneficialOwners: updatedUBOs });
	};

	const updateUBO = (
		tierIndex: number,
		uboIndex: number,
		updates: Partial<KYCUltimateBeneficialOwner>,
	) => {
		const tier = formData.ownershipTiers[tierIndex];
		const updatedUBOs = [...(tier.ultimateBeneficialOwners || [])];
		updatedUBOs[uboIndex] = { ...updatedUBOs[uboIndex], ...updates };
		updateTier(tierIndex, { ultimateBeneficialOwners: updatedUBOs });
	};

	// Director management functions
	const addDirector = () => {
		const newDirector: KYCDirector = {
			isExistingOwner: false,
		};
		const updatedDirectors = [
			...(formData.boardOfDirectors || []),
			newDirector,
		];
		updateFormData({ boardOfDirectors: updatedDirectors });
	};

	const removeDirector = (directorIndex: number) => {
		const updatedDirectors = (formData.boardOfDirectors || []).filter(
			(_, i) => i !== directorIndex,
		);
		updateFormData({ boardOfDirectors: updatedDirectors });
	};

	const updateDirector = (
		directorIndex: number,
		updates: Partial<KYCDirector>,
	) => {
		const updatedDirectors = [...(formData.boardOfDirectors || [])];
		updatedDirectors[directorIndex] = {
			...updatedDirectors[directorIndex],
			...updates,
		};
		updateFormData({ boardOfDirectors: updatedDirectors });
	};

	const handleDirectorSourceChange = (directorIndex: number, value: string) => {
		if (value === "new") {
			updateDirector(directorIndex, {
				isExistingOwner: false,
				ownerIndex: undefined,
			});
		} else {
			const ownerIdx = Number.parseInt(value, 10);
			updateDirector(directorIndex, {
				isExistingOwner: true,
				ownerIndex: ownerIdx,
			});
		}
	};

	return (
		<div className="w-full">
			<p className="mb-4 text-(--color-muted-foreground)">
				Add owners holding {thresholds.minOwnershipPercentage}% or more of the
				company. You can add multiple owners as needed. For corporate owners
				with sub-ownership of {thresholds.uboTrigger}% or more, add UBO details.
			</p>

			{formData.ownershipTiers.map((tier, index) => (
				<OwnershipTierCard
					key={`ownership-tier-${index}-${tier.tierNumber || index}-${tier.ownerType || "unknown"}`}
					tierNumber={tier.tierNumber ?? index + 1}
					summary={tierSummary(tier)}
					canRemove={formData.ownershipTiers.length > 1}
					onRemove={() => removeTier(index)}
				>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							{/* biome-ignore lint/a11y/noLabelWithoutControl: Radio group labels are associated with inputs */}
							<Label>
								Owner Type
								<span
									aria-hidden="true"
									className="ml-0.5 text-(--color-error-500)"
								>
									*
								</span>
							</Label>
							<div className="flex flex-col gap-3">
								<label className="flex cursor-pointer items-center gap-2 font-normal">
									<input
										type="radio"
										name={`ownerType-${index}`}
										value="Individual"
										checked={tier.ownerType === "Individual"}
										onChange={() =>
											updateTier(index, { ownerType: "Individual" })
										}
									/>
									<span>Individual</span>
								</label>
								<label className="flex cursor-pointer items-center gap-2 font-normal">
									<input
										type="radio"
										name={`ownerType-${index}`}
										value="Corporation"
										checked={tier.ownerType === "Corporation"}
										onChange={() =>
											updateTier(index, { ownerType: "Corporation" })
										}
									/>
									<span>Corporation</span>
								</label>
							</div>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor={`ownership-percentage-${index}`}>
								Ownership Percentage
								<span
									aria-hidden="true"
									className="ml-0.5 text-(--color-error-500)"
								>
									*
								</span>
							</Label>
							<Input
								id={`ownership-percentage-${index}`}
								type="number"
								value={tier.ownershipPercentage || ""}
								onChange={(e) =>
									updateTier(index, {
										ownershipPercentage: Number(e.target.value),
									})
								}
								min={thresholds.minOwnershipPercentage}
								max={100}
								className={
									tier.ownershipPercentage > 0 &&
									tier.ownershipPercentage < thresholds.minOwnershipPercentage
										? "border-(--color-error-500)"
										: undefined
								}
							/>
							{tier.ownershipPercentage > 0 &&
								tier.ownershipPercentage < 10 && (
									<p className="mt-1 text-(--color-error-500) text-[0.813rem]">
										Ownership must be at least 10%
									</p>
								)}
						</div>
					</div>

					{tier.ownerType === "Corporation" &&
						tier.ownershipPercentage >= thresholds.minOwnershipPercentage && (
							<>
								<div className="my-6 h-px bg-gradient-to-r from-transparent via-(--color-border) to-transparent opacity-60" />
								<h5 style={{ marginBottom: "16px", fontSize: "0.938rem" }}>
									Corporate Owner Information (≥10% ownership)
								</h5>

								<div className="space-y-1.5">
									<Label htmlFor={`company-name-${index}`}>
										Company Name
										<span
											aria-hidden="true"
											className="ml-0.5 text-(--color-error-500)"
										>
											*
										</span>
									</Label>
									<Input
										id={`company-name-${index}`}
										type="text"
										value={tier.companyName || ""}
										onChange={(e) =>
											updateTier(index, { companyName: e.target.value })
										}
										placeholder="Legal company name"
									/>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor={`registration-number-${index}`}>
											Registration/Company Number
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`registration-number-${index}`}
											type="text"
											value={tier.registrationNumber || ""}
											onChange={(e) =>
												updateTier(index, {
													registrationNumber: e.target.value,
												})
											}
											placeholder="Company registration number"
										/>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor={`date-of-incorporation-${index}`}>
											Date of Incorporation
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<DatePicker
											id={`date-of-incorporation-${index}`}
											value={tier.dateOfIncorporation || ""}
											onChange={(val) =>
												updateTier(index, {
													dateOfIncorporation: val,
												})
											}
											maxDate={new Date().toISOString().slice(0, 10)}
										/>
									</div>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor={`registered-office-address-${index}`}>
										Registered Office Address
										<span
											aria-hidden="true"
											className="ml-0.5 text-(--color-error-500)"
										>
											*
										</span>
									</Label>
									<Textarea
										id={`registered-office-address-${index}`}
										value={tier.registeredOfficeAddress || ""}
										onChange={(e) =>
											updateTier(index, {
												registeredOfficeAddress: e.target.value,
											})
										}
										placeholder="Full registered office address"
										rows={3}
									/>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor={`tax-id-${index}`}>
											Tax ID
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`tax-id-${index}`}
											type="text"
											value={tier.taxId || ""}
											onChange={(e) =>
												updateTier(index, { taxId: e.target.value })
											}
											placeholder="Tax identification number"
										/>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor={`legal-entity-type-${index}`}>
											Legal Entity Type
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`legal-entity-type-${index}`}
											type="text"
											value={tier.legalEntityType || ""}
											onChange={(e) =>
												updateTier(index, { legalEntityType: e.target.value })
											}
											placeholder="e.g., LLC, Corporation, Partnership"
										/>
									</div>
								</div>

								{/* Nested UBO Section */}
								<div className="my-6 h-px bg-gradient-to-r from-transparent via-(--color-border) to-transparent opacity-60" />
								<div style={{ marginBottom: "16px" }}>
									<h5 style={{ marginBottom: "8px", fontSize: "0.938rem" }}>
										Ultimate Beneficial Owners (UBO) of{" "}
										{tier.companyName || "this company"}
									</h5>
									<p className="text-(--color-muted-foreground) text-sm">
										Please provide details of individuals or companies holding
										25% or more ownership in this company.
									</p>
								</div>

								{(tier.ultimateBeneficialOwners || []).map((ubo, uboIndex) => (
									<div
										key={`ubo-${index}-${uboIndex}`}
										className="mb-4 rounded-(--radius-sm) border border-(--color-border) bg-(--color-muted) p-4"
									>
										<div className="mb-3 flex items-center justify-between">
											<h6
												style={{
													margin: 0,
													fontSize: "0.875rem",
													fontWeight: 600,
												}}
											>
												UBO {uboIndex + 1}
											</h6>
											<button
												type="button"
												onClick={() => removeUBO(index, uboIndex)}
												className="flex cursor-pointer items-center gap-1 border-none bg-transparent px-1.5 py-0.5 text-(--color-error-500) text-[0.813rem]"
											>
												<X size={14} />
												Remove
											</button>
										</div>

										<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
											<div className="space-y-1.5">
												{/* biome-ignore lint/a11y/noLabelWithoutControl: Radio group labels */}
												<Label>
													Owner Type
													<span
														aria-hidden="true"
														className="ml-0.5 text-(--color-error-500)"
													>
														*
													</span>
												</Label>
												<div className="flex flex-col gap-3">
													<label className="flex cursor-pointer items-center gap-2 font-normal">
														<input
															type="radio"
															name={`ubo-type-${index}-${uboIndex}`}
															value="Individual"
															checked={ubo.ownerType === "Individual"}
															onChange={() =>
																updateUBO(index, uboIndex, {
																	ownerType: "Individual",
																})
															}
														/>
														<span>Individual</span>
													</label>
													<label className="flex cursor-pointer items-center gap-2 font-normal">
														<input
															type="radio"
															name={`ubo-type-${index}-${uboIndex}`}
															value="Corporation"
															checked={ubo.ownerType === "Corporation"}
															onChange={() =>
																updateUBO(index, uboIndex, {
																	ownerType: "Corporation",
																})
															}
														/>
														<span>Corporation</span>
													</label>
												</div>
											</div>

											<div className="space-y-1.5">
												<Label htmlFor={`ubo-percentage-${index}-${uboIndex}`}>
													Ownership %
													<span
														aria-hidden="true"
														className="ml-0.5 text-(--color-error-500)"
													>
														*
													</span>
												</Label>
												<Input
													id={`ubo-percentage-${index}-${uboIndex}`}
													type="number"
													value={ubo.ownershipPercentage || ""}
													onChange={(e) =>
														updateUBO(index, uboIndex, {
															ownershipPercentage: Number(e.target.value),
														})
													}
													min={25}
													max={100}
													className={
														ubo.ownershipPercentage > 0 &&
														ubo.ownershipPercentage < 25
															? "border-(--color-error-500)"
															: undefined
													}
												/>
												{ubo.ownershipPercentage > 0 &&
													ubo.ownershipPercentage < 25 && (
														<p className="mt-1 text-(--color-error-500) text-[0.813rem]">
															UBO ownership must be at least 25%
														</p>
													)}
											</div>
										</div>

										{ubo.ownershipPercentage >= thresholds.uboTrigger && (
											<>
												{ubo.ownerType === "Individual" && (
													<>
														<div className="space-y-1.5">
															<Label htmlFor={`ubo-name-${index}-${uboIndex}`}>
																Full Name
																<span
																	aria-hidden="true"
																	className="ml-0.5 text-(--color-error-500)"
																>
																	*
																</span>
															</Label>
															<Input
																id={`ubo-name-${index}-${uboIndex}`}
																type="text"
																value={ubo.fullName || ""}
																onChange={(e) =>
																	updateUBO(index, uboIndex, {
																		fullName: e.target.value,
																	})
																}
															/>
														</div>

														<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
															<div className="space-y-1.5">
																<Label
																	htmlFor={`ubo-nationality-${index}-${uboIndex}`}
																>
																	Nationality
																	<span
																		aria-hidden="true"
																		className="ml-0.5 text-(--color-error-500)"
																	>
																		*
																	</span>
																</Label>
																<Select
																	value={ubo.nationality || ""}
																	onValueChange={(value) =>
																		updateUBO(index, uboIndex, {
																			nationality: value,
																		})
																	}
																>
																	<SelectTrigger
																		id={`ubo-nationality-${index}-${uboIndex}`}
																	>
																		<SelectValue placeholder="Select nationality" />
																	</SelectTrigger>
																	<SelectContent>
																		{OPTION_LISTS.COUNTRIES_ISO.map((opt) => (
																			<SelectItem
																				key={opt.value}
																				value={opt.value}
																			>
																				{opt.label.en}
																			</SelectItem>
																		))}
																	</SelectContent>
																</Select>
															</div>

															<div className="space-y-1.5">
																<Label htmlFor={`ubo-dob-${index}-${uboIndex}`}>
																	Date of Birth
																	<span
																		aria-hidden="true"
																		className="ml-0.5 text-(--color-error-500)"
																	>
																		*
																	</span>
																</Label>
																<DatePicker
																	id={`ubo-dob-${index}-${uboIndex}`}
																	value={ubo.dateOfBirth || ""}
																	onChange={(val) =>
																		updateUBO(index, uboIndex, {
																			dateOfBirth: val,
																		})
																	}
																	maxDate={new Date()
																		.toISOString()
																		.slice(0, 10)}
																/>
															</div>
														</div>

														<div className="space-y-1.5">
															<Label
																htmlFor={`ubo-country-${index}-${uboIndex}`}
															>
																Country of Residence
																<span
																	aria-hidden="true"
																	className="ml-0.5 text-(--color-error-500)"
																>
																	*
																</span>
															</Label>
															<Select
																value={ubo.countryOfResidence || ""}
																onValueChange={(value) =>
																	updateUBO(index, uboIndex, {
																		countryOfResidence: value,
																		stateOrProvince: undefined,
																		ssn: undefined,
																		sin: undefined,
																	})
																}
															>
																<SelectTrigger
																	id={`ubo-country-${index}-${uboIndex}`}
																>
																	<SelectValue placeholder="Select country" />
																</SelectTrigger>
																<SelectContent>
																	{OPTION_LISTS.COUNTRIES_ISO.map((opt) => (
																		<SelectItem
																			key={opt.value}
																			value={opt.value}
																		>
																			{opt.label.en}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</div>

														<div className="space-y-1.5">
															<Label
																htmlFor={`ubo-street-${index}-${uboIndex}`}
															>
																Street Address
																<span
																	aria-hidden="true"
																	className="ml-0.5 text-(--color-error-500)"
																>
																	*
																</span>
															</Label>
															<Input
																id={`ubo-street-${index}-${uboIndex}`}
																type="text"
																value={ubo.residentialAddress || ""}
																onChange={(e) =>
																	updateUBO(index, uboIndex, {
																		residentialAddress: e.target.value,
																	})
																}
															/>
														</div>

														<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
															<div className="space-y-1.5">
																<Label
																	htmlFor={`ubo-city-${index}-${uboIndex}`}
																>
																	City
																	<span
																		aria-hidden="true"
																		className="ml-0.5 text-(--color-error-500)"
																	>
																		*
																	</span>
																</Label>
																<Input
																	id={`ubo-city-${index}-${uboIndex}`}
																	type="text"
																	value={ubo.city || ""}
																	onChange={(e) =>
																		updateUBO(index, uboIndex, {
																			city: e.target.value,
																		})
																	}
																/>
															</div>

															{(ubo.countryOfResidence === "US" ||
																ubo.countryOfResidence === "CA") && (
																<div className="space-y-1.5">
																	<Label
																		htmlFor={`ubo-state-${index}-${uboIndex}`}
																	>
																		{ubo.countryOfResidence === "US"
																			? "State"
																			: "Province"}
																		<span
																			aria-hidden="true"
																			className="ml-0.5 text-(--color-error-500)"
																		>
																			*
																		</span>
																	</Label>
																	<Select
																		value={ubo.stateOrProvince || ""}
																		onValueChange={(value) =>
																			updateUBO(index, uboIndex, {
																				stateOrProvince: value,
																			})
																		}
																	>
																		<SelectTrigger
																			id={`ubo-state-${index}-${uboIndex}`}
																		>
																			<SelectValue
																				placeholder={
																					ubo.countryOfResidence === "US"
																						? "Select state"
																						: "Select province"
																				}
																			/>
																		</SelectTrigger>
																		<SelectContent>
																			{(ubo.countryOfResidence === "US"
																				? US_STATES
																				: CANADIAN_PROVINCES
																			).map((loc) => (
																				<SelectItem key={loc} value={loc}>
																					{loc}
																				</SelectItem>
																			))}
																		</SelectContent>
																	</Select>
																</div>
															)}
														</div>

														<div className="space-y-1.5">
															<Label
																htmlFor={`ubo-postal-${index}-${uboIndex}`}
															>
																{ubo.countryOfResidence === "US"
																	? "ZIP Code"
																	: "Postal Code"}
																<span
																	aria-hidden="true"
																	className="ml-0.5 text-(--color-error-500)"
																>
																	*
																</span>
															</Label>
															<Input
																id={`ubo-postal-${index}-${uboIndex}`}
																type="text"
																value={ubo.postalCode || ""}
																onChange={(e) =>
																	updateUBO(index, uboIndex, {
																		postalCode: e.target.value,
																	})
																}
															/>
														</div>
													</>
												)}

												{ubo.ownerType === "Corporation" && (
													<>
														<div className="space-y-1.5">
															<Label
																htmlFor={`ubo-company-${index}-${uboIndex}`}
															>
																Company Name
																<span
																	aria-hidden="true"
																	className="ml-0.5 text-(--color-error-500)"
																>
																	*
																</span>
															</Label>
															<Input
																id={`ubo-company-${index}-${uboIndex}`}
																type="text"
																value={ubo.companyName || ""}
																onChange={(e) =>
																	updateUBO(index, uboIndex, {
																		companyName: e.target.value,
																	})
																}
																placeholder="Legal company name"
															/>
														</div>

														<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
															<div className="space-y-1.5">
																<Label
																	htmlFor={`ubo-reg-number-${index}-${uboIndex}`}
																>
																	Registration/Company Number
																	<span
																		aria-hidden="true"
																		className="ml-0.5 text-(--color-error-500)"
																	>
																		*
																	</span>
																</Label>
																<Input
																	id={`ubo-reg-number-${index}-${uboIndex}`}
																	type="text"
																	value={ubo.registrationNumber || ""}
																	onChange={(e) =>
																		updateUBO(index, uboIndex, {
																			registrationNumber: e.target.value,
																		})
																	}
																	placeholder="Company registration number"
																/>
															</div>

															<div className="space-y-1.5">
																<Label
																	htmlFor={`ubo-inc-date-${index}-${uboIndex}`}
																>
																	Date of Incorporation
																	<span
																		aria-hidden="true"
																		className="ml-0.5 text-(--color-error-500)"
																	>
																		*
																	</span>
																</Label>
																<DatePicker
																	id={`ubo-inc-date-${index}-${uboIndex}`}
																	value={ubo.dateOfIncorporation || ""}
																	onChange={(val) =>
																		updateUBO(index, uboIndex, {
																			dateOfIncorporation: val,
																		})
																	}
																	maxDate={new Date()
																		.toISOString()
																		.slice(0, 10)}
																/>
															</div>
														</div>

														<div className="space-y-1.5">
															<Label
																htmlFor={`ubo-reg-address-${index}-${uboIndex}`}
															>
																Registered Office Address
																<span
																	aria-hidden="true"
																	className="ml-0.5 text-(--color-error-500)"
																>
																	*
																</span>
															</Label>
															<Textarea
																id={`ubo-reg-address-${index}-${uboIndex}`}
																value={ubo.registeredOfficeAddress || ""}
																onChange={(e) =>
																	updateUBO(index, uboIndex, {
																		registeredOfficeAddress: e.target.value,
																	})
																}
																placeholder="Full registered office address"
																rows={2}
															/>
														</div>

														<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
															<div className="space-y-1.5">
																<Label
																	htmlFor={`ubo-tax-id-${index}-${uboIndex}`}
																>
																	Tax ID
																	<span
																		aria-hidden="true"
																		className="ml-0.5 text-(--color-error-500)"
																	>
																		*
																	</span>
																</Label>
																<Input
																	id={`ubo-tax-id-${index}-${uboIndex}`}
																	type="text"
																	value={ubo.taxId || ""}
																	onChange={(e) =>
																		updateUBO(index, uboIndex, {
																			taxId: e.target.value,
																		})
																	}
																	placeholder="Tax identification number"
																/>
															</div>

															<div className="space-y-1.5">
																<Label
																	htmlFor={`ubo-entity-type-${index}-${uboIndex}`}
																>
																	Legal Entity Type
																	<span
																		aria-hidden="true"
																		className="ml-0.5 text-(--color-error-500)"
																	>
																		*
																	</span>
																</Label>
																<Input
																	id={`ubo-entity-type-${index}-${uboIndex}`}
																	type="text"
																	value={ubo.legalEntityType || ""}
																	onChange={(e) =>
																		updateUBO(index, uboIndex, {
																			legalEntityType: e.target.value,
																		})
																	}
																	placeholder="e.g., LLC, Corporation, Partnership"
																/>
															</div>
														</div>
													</>
												)}
											</>
										)}
									</div>
								))}

								<button
									type="button"
									className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-[18px] py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)"
									onClick={() => addUBO(index)}
									style={{
										marginTop: "8px",
										fontSize: "0.875rem",
										width: "auto",
										display: "inline-flex",
										alignItems: "center",
										gap: "8px",
									}}
								>
									<Plus size={16} />
									Add UBO
								</button>
							</>
						)}

					{tier.ownerType === "Individual" &&
						tier.ownershipPercentage >= thresholds.minOwnershipPercentage && (
							<>
								<div className="my-6 h-px bg-gradient-to-r from-transparent via-(--color-border) to-transparent opacity-60" />
								<h5 className="mb-4 text-[0.938rem]">
									Individual Owner Information (≥
									{thresholds.minOwnershipPercentage}% ownership)
								</h5>

								<div className="space-y-1.5">
									<Label htmlFor={`full-name-${index}`}>
										Full Name
										<span
											aria-hidden="true"
											className="ml-0.5 text-(--color-error-500)"
										>
											*
										</span>
									</Label>
									<Input
										id={`full-name-${index}`}
										type="text"
										value={tier.fullName || ""}
										onChange={(e) =>
											updateTier(index, { fullName: e.target.value })
										}
									/>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor={`date-of-birth-${index}`}>
											Date of Birth
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<DatePicker
											id={`date-of-birth-${index}`}
											value={tier.dateOfBirth || ""}
											onChange={(val) =>
												updateTier(index, { dateOfBirth: val })
											}
											maxDate={new Date().toISOString().slice(0, 10)}
										/>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor={`nationality-${index}`}>
											Nationality
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Select
											value={tier.nationality || ""}
											onValueChange={(value) =>
												updateTier(index, { nationality: value })
											}
										>
											<SelectTrigger id={`nationality-${index}`}>
												<SelectValue placeholder="Select nationality" />
											</SelectTrigger>
											<SelectContent>
												{OPTION_LISTS.COUNTRIES_ISO.map((opt) => (
													<SelectItem key={opt.value} value={opt.value}>
														{opt.label.en}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor={`country-of-residence-${index}`}>
										Country of Residence
										<span
											aria-hidden="true"
											className="ml-0.5 text-(--color-error-500)"
										>
											*
										</span>
									</Label>
									<Select
										value={tier.countryOfResidence || ""}
										onValueChange={(value) =>
											updateTier(index, {
												countryOfResidence: value,
												stateOrProvince: undefined,
												ssn: undefined,
												sin: undefined,
											})
										}
									>
										<SelectTrigger id={`country-of-residence-${index}`}>
											<SelectValue placeholder="Select country" />
										</SelectTrigger>
										<SelectContent>
											{OPTION_LISTS.COUNTRIES_ISO.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label.en}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor={`street-address-${index}`}>
										Street Address
										<span
											aria-hidden="true"
											className="ml-0.5 text-(--color-error-500)"
										>
											*
										</span>
									</Label>
									<Input
										id={`street-address-${index}`}
										type="text"
										value={tier.residentialAddress || ""}
										onChange={(e) =>
											updateTier(index, { residentialAddress: e.target.value })
										}
									/>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor={`city-${index}`}>
											City
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`city-${index}`}
											type="text"
											value={tier.city || ""}
											onChange={(e) =>
												updateTier(index, { city: e.target.value })
											}
										/>
									</div>

									{(tier.countryOfResidence === "US" ||
										tier.countryOfResidence === "CA") && (
										<div className="space-y-1.5">
											<Label htmlFor={`state-or-province-${index}`}>
												{tier.countryOfResidence === "US"
													? "State"
													: "Province"}
												<span
													aria-hidden="true"
													className="ml-0.5 text-(--color-error-500)"
												>
													*
												</span>
											</Label>
											<Select
												value={tier.stateOrProvince || ""}
												onValueChange={(value) =>
													updateTier(index, { stateOrProvince: value })
												}
											>
												<SelectTrigger id={`state-or-province-${index}`}>
													<SelectValue
														placeholder={
															tier.countryOfResidence === "US"
																? "Select state"
																: "Select province"
														}
													/>
												</SelectTrigger>
												<SelectContent>
													{(tier.countryOfResidence === "US"
														? US_STATES
														: CANADIAN_PROVINCES
													).map((loc) => (
														<SelectItem key={loc} value={loc}>
															{loc}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)}
								</div>

								<div className="space-y-1.5">
									<Label htmlFor={`postal-code-${index}`}>
										{tier.countryOfResidence === "US"
											? "ZIP Code"
											: "Postal Code"}
										<span
											aria-hidden="true"
											className="ml-0.5 text-(--color-error-500)"
										>
											*
										</span>
									</Label>
									<Input
										id={`postal-code-${index}`}
										type="text"
										value={tier.postalCode || ""}
										onChange={(e) =>
											updateTier(index, { postalCode: e.target.value })
										}
									/>
								</div>

								{(tier.countryOfResidence === "US" ||
									tier.countryOfResidence === "PR") && (
									<div className="space-y-1.5">
										<Label htmlFor={`ssn-${index}`}>
											Social Security Number (SSN)
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`ssn-${index}`}
											type="text"
											value={tier.ssn || ""}
											onChange={(e) =>
												updateTier(index, { ssn: e.target.value })
											}
											maxLength={11}
										/>
									</div>
								)}

								{tier.countryOfResidence === "CA" && (
									<div className="space-y-1.5">
										<Label htmlFor={`sin-${index}`}>
											Social Insurance Number (SIN)
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`sin-${index}`}
											type="text"
											value={tier.sin || ""}
											onChange={(e) =>
												updateTier(index, { sin: e.target.value })
											}
											maxLength={11}
										/>
									</div>
								)}

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor={`photo-id-${index}`}>
											Photo ID
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<FileUploadField
											compact
											id={`photo-id-${index}`}
											label="Photo ID"
											required
											value={tier.photoIdUrl}
											onUpload={(key) => updateTier(index, { photoIdUrl: key })}
											onRemove={() =>
												updateTier(index, { photoIdUrl: undefined })
											}
										/>
									</div>

									{tier.countryOfResidence === "CA" && (
										<div className="space-y-1.5">
											<Label htmlFor={`second-photo-id-${index}`}>
												Second Photo ID
												<span
													aria-hidden="true"
													className="ml-0.5 text-(--color-error-500)"
												>
													*
												</span>
											</Label>
											<FileUploadField
												compact
												id={`second-photo-id-${index}`}
												label="Second Photo ID"
												required
												value={tier.secondPhotoIdUrl}
												onUpload={(key) =>
													updateTier(index, {
														secondPhotoIdUrl: key,
													})
												}
												onRemove={() =>
													updateTier(index, {
														secondPhotoIdUrl: undefined,
													})
												}
											/>
										</div>
									)}
								</div>

								{/* Consent Forms Section - Only for Georgia, Nevada, or Canada */}
								{(tier.countryOfResidence === "US" &&
									(tier.stateOrProvince === "Georgia" ||
										tier.stateOrProvince === "Nevada")) ||
								tier.countryOfResidence === "CA" ? (
									<div className="mt-6 rounded-(--radius-sm) border border-(--color-border) bg-(--color-muted) p-5">
										<h4 className="mb-3 font-semibold text-(--color-foreground) text-[0.938rem]">
											Required Consent Form
											{tier.countryOfResidence === "CA" ? "s" : ""}
											<span className="ml-1 text-(--color-error-500)">*</span>
										</h4>
										<p className="mb-4 text-(--color-muted-foreground) text-sm">
											Based on your location, you must download, complete, sign,
											and upload the following form(s):
										</p>

										{/* Georgia Consent Form */}
										{tier.countryOfResidence === "US" &&
											tier.stateOrProvince === "Georgia" && (
												<div className="mb-4">
													<button
														type="button"
														onClick={() => handleConsentFormDownload("georgia")}
														className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-[6px] border-none bg-(--color-primary) px-4 py-2 font-medium text-(--color-primary-foreground) text-sm hover:bg-(--color-primary-hover)"
													>
														<Upload size={16} />
														Download Georgia Consent Form
													</button>
													<div className="mt-3 space-y-1.5">
														<Label htmlFor={`consent-form-georgia-${index}`}>
															Upload Signed Form
															<span
																aria-hidden="true"
																className="ml-0.5 text-(--color-error-500)"
															>
																*
															</span>
														</Label>
														<FileUploadField
															compact
															id={`consent-form-georgia-${index}`}
															label="Signed Consent Form"
															required
															accept=".pdf"
															value={tier.consentFormUrl}
															onUpload={(key) =>
																updateTier(index, {
																	consentFormUrl: key,
																})
															}
															onRemove={() =>
																updateTier(index, {
																	consentFormUrl: undefined,
																})
															}
														/>
													</div>
												</div>
											)}

										{/* Nevada Consent Form */}
										{tier.countryOfResidence === "US" &&
											tier.stateOrProvince === "Nevada" && (
												<div className="mb-4">
													<button
														type="button"
														onClick={() => handleConsentFormDownload("nevada")}
														className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-[6px] border-none bg-(--color-primary) px-4 py-2 font-medium text-(--color-primary-foreground) text-sm hover:bg-(--color-primary-hover)"
													>
														<Upload size={16} />
														Download Nevada Consent Form
													</button>
													<div className="mt-3 space-y-1.5">
														<Label htmlFor={`consent-form-nevada-${index}`}>
															Upload Signed Form
															<span
																aria-hidden="true"
																className="ml-0.5 text-(--color-error-500)"
															>
																*
															</span>
														</Label>
														<FileUploadField
															compact
															id={`consent-form-nevada-${index}`}
															label="Signed Consent Form"
															required
															accept=".pdf"
															value={tier.consentFormUrl}
															onUpload={(key) =>
																updateTier(index, {
																	consentFormUrl: key,
																})
															}
															onRemove={() =>
																updateTier(index, {
																	consentFormUrl: undefined,
																})
															}
														/>
													</div>
												</div>
											)}

										{/* Canada Consent Forms (2 required) */}
										{tier.countryOfResidence === "CA" && (
											<>
												{/* First Canada Form */}
												<div className="mb-5">
													<button
														type="button"
														onClick={() =>
															handleConsentFormDownload("canada-federal")
														}
														className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-[6px] border-none bg-(--color-primary) px-4 py-2 font-medium text-(--color-primary-foreground) text-sm hover:bg-(--color-primary-hover)"
													>
														<Upload size={16} />
														Download Canada Federal Consent Form
													</button>
													<div className="mt-3 space-y-1.5">
														<Label
															htmlFor={`consent-form-canada-federal-${index}`}
														>
															Upload Signed Federal Form
															<span
																aria-hidden="true"
																className="ml-0.5 text-(--color-error-500)"
															>
																*
															</span>
														</Label>
														<FileUploadField
															compact
															id={`consent-form-canada-federal-${index}`}
															label="Signed Federal Form"
															required
															accept=".pdf"
															value={tier.consentFormUrl}
															onUpload={(key) =>
																updateTier(index, {
																	consentFormUrl: key,
																})
															}
															onRemove={() =>
																updateTier(index, {
																	consentFormUrl: undefined,
																})
															}
														/>
													</div>
												</div>

												{/* Second Canada Form */}
												<div>
													<button
														type="button"
														onClick={() =>
															handleConsentFormDownload("canada-provincial")
														}
														className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-[6px] border-none bg-(--color-primary) px-4 py-2 font-medium text-(--color-primary-foreground) text-sm hover:bg-(--color-primary-hover)"
													>
														<Upload size={16} />
														Download Canada Provincial Consent Form
													</button>
													<div className="mt-3 space-y-1.5">
														<Label htmlFor={`second-consent-form-${index}`}>
															Upload Signed Provincial Form
															<span
																aria-hidden="true"
																className="ml-0.5 text-(--color-error-500)"
															>
																*
															</span>
														</Label>
														<FileUploadField
															compact
															id={`second-consent-form-${index}`}
															label="Signed Provincial Form"
															required
															accept=".pdf"
															value={tier.secondConsentFormUrl}
															onUpload={(key) =>
																updateTier(index, {
																	secondConsentFormUrl: key,
																})
															}
															onRemove={() =>
																updateTier(index, {
																	secondConsentFormUrl: undefined,
																})
															}
														/>
													</div>
												</div>
											</>
										)}
									</div>
								) : null}
							</>
						)}
				</OwnershipTierCard>
			))}

			<button
				type="button"
				className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-[18px] py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)"
				onClick={addTier}
				style={{
					marginBottom: "24px",
					width: "auto",
					display: "inline-flex",
					alignItems: "center",
					gap: "8px",
				}}
			>
				<Plus size={16} />
				Add Owner
			</button>

			<div className="my-6 h-px bg-gradient-to-r from-transparent via-(--color-border) to-transparent opacity-60" />

			<div style={{ marginBottom: "24px" }}>
				<FileUploadField
					id="ownership-chart"
					label="Full Ownership Structure Chart"
					required
					accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
					description="Upload a complete organizational chart showing ownership structure on company letterhead"
					value={formData.ownershipStructureChartUrl}
					onUpload={(key) =>
						updateFormData({ ownershipStructureChartUrl: key })
					}
					onRemove={() =>
						updateFormData({ ownershipStructureChartUrl: undefined })
					}
				/>
			</div>

			<div className="my-6 h-px bg-gradient-to-r from-transparent via-(--color-border) to-transparent opacity-60" />

			<div className="mb-5">
				{/* biome-ignore lint/a11y/noLabelWithoutControl: Question label for button group */}
				<Label className="mb-2 block">
					Does the company have a Board of Directors?
					<span aria-hidden="true" className="ml-0.5 text-(--color-error-500)">
						*
					</span>
				</Label>
				<div className="flex max-w-[400px] gap-3">
					<button
						type="button"
						className={cn(
							"flex-1 cursor-pointer rounded-sm border border-(--color-border) bg-(--color-muted) px-5 py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)",
							formData.hasBoardOfDirectors &&
								"border-(--color-primary-500) bg-white font-semibold text-(--color-primary-500) ring-(--color-primary-100) ring-[3px]",
						)}
						onClick={() => updateFormData({ hasBoardOfDirectors: true })}
					>
						Yes
					</button>
					<button
						type="button"
						className={cn(
							"flex-1 cursor-pointer rounded-sm border border-(--color-border) bg-(--color-muted) px-5 py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)",
							formData.hasBoardOfDirectors === false &&
								"border-(--color-primary-500) bg-white font-semibold text-(--color-primary-500) ring-(--color-primary-100) ring-[3px]",
						)}
						onClick={() =>
							updateFormData({
								hasBoardOfDirectors: false,
								boardOfDirectors: [],
							})
						}
					>
						No
					</button>
				</div>
			</div>

			{formData.hasBoardOfDirectors && (
				<div className="mt-4 rounded-(--radius-lg) border border-(--color-border) bg-(--color-muted) p-5">
					<h5 style={{ marginBottom: "16px", fontSize: "0.938rem" }}>
						Board of Directors
					</h5>
					<p className="mb-4 text-(--color-muted-foreground) text-sm">
						Select owners who are also directors, or add new directors who are
						not owners.
					</p>

					{(formData.boardOfDirectors || []).map((director, directorIndex) => (
						<div
							key={`director-${directorIndex}`}
							className="mb-4 rounded-sm border border-(--color-border) bg-(--color-muted) p-4"
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: "12px",
								}}
							>
								<h6
									style={{
										margin: 0,
										fontSize: "0.875rem",
										fontWeight: 600,
									}}
								>
									Director {directorIndex + 1}
								</h6>
								<button
									type="button"
									onClick={() => removeDirector(directorIndex)}
									style={{
										padding: "2px 6px",
										background: "transparent",
										border: "none",
										color: "var(--color-destructive)",
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										gap: "4px",
										fontSize: "0.813rem",
									}}
								>
									<X size={14} />
									Remove
								</button>
							</div>

							<div className="mb-5 space-y-1.5">
								<Label htmlFor={`director-source-${directorIndex}`}>
									Director Type
									<span
										aria-hidden="true"
										className="ml-0.5 text-(--color-error-500)"
									>
										*
									</span>
								</Label>
								<Select
									value={
										director.isExistingOwner
											? (director.ownerIndex?.toString() ?? "new")
											: "new"
									}
									onValueChange={(value) =>
										handleDirectorSourceChange(directorIndex, value)
									}
								>
									<SelectTrigger id={`director-source-${directorIndex}`}>
										<SelectValue placeholder="Select director type" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="new">
											New Director (not an owner)
										</SelectItem>
										{formData.ownershipTiers
											.filter(
												(t) =>
													t.ownerType === "Individual" &&
													(t.ownershipPercentage ?? 0) >=
														thresholds.minOwnershipPercentage,
											)
											.map((t, idx) => (
												<SelectItem key={idx} value={idx.toString()}>
													Owner {t.tierNumber}: {t.fullName || "Unnamed"}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>

							{!director.isExistingOwner && (
								<>
									<div className="mb-5 space-y-1.5">
										<Label htmlFor={`director-name-${directorIndex}`}>
											Full Name
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Input
											id={`director-name-${directorIndex}`}
											type="text"
											value={director.fullName || ""}
											onChange={(e) =>
												updateDirector(directorIndex, {
													fullName: e.target.value,
												})
											}
											placeholder="Full legal name"
										/>
									</div>

									<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
										<div className="mb-5 space-y-1.5">
											<Label htmlFor={`director-dob-${directorIndex}`}>
												Date of Birth
												<span
													aria-hidden="true"
													className="ml-0.5 text-(--color-error-500)"
												>
													*
												</span>
											</Label>
											<DatePicker
												id={`director-dob-${directorIndex}`}
												value={director.dateOfBirth || ""}
												onChange={(val) =>
													updateDirector(directorIndex, {
														dateOfBirth: val,
													})
												}
												maxDate={new Date().toISOString().slice(0, 10)}
											/>
										</div>

										<div className="mb-5 space-y-1.5">
											<Label htmlFor={`director-nationality-${directorIndex}`}>
												Nationality
												<span
													aria-hidden="true"
													className="ml-0.5 text-(--color-error-500)"
												>
													*
												</span>
											</Label>
											<Input
												id={`director-nationality-${directorIndex}`}
												type="text"
												value={director.nationality || ""}
												onChange={(e) =>
													updateDirector(directorIndex, {
														nationality: e.target.value,
													})
												}
												placeholder="Nationality"
											/>
										</div>
									</div>

									<div className="mb-5 space-y-1.5">
										<Label htmlFor={`director-address-${directorIndex}`}>
											Address
											<span
												aria-hidden="true"
												className="ml-0.5 text-(--color-error-500)"
											>
												*
											</span>
										</Label>
										<Textarea
											id={`director-address-${directorIndex}`}
											value={director.address || ""}
											onChange={(e) =>
												updateDirector(directorIndex, {
													address: e.target.value,
												})
											}
											placeholder="Full address"
											rows={2}
										/>
									</div>
								</>
							)}

							{director.isExistingOwner && (
								<div className="rounded-[6px] border border-(--color-success-300) bg-(--color-success-50) p-3 text-(--color-success-700) text-sm">
									✓ Director information will be pulled from Owner{" "}
									{director.ownerIndex !== undefined
										? formData.ownershipTiers[director.ownerIndex]?.tierNumber
										: ""}{" "}
									details
								</div>
							)}
						</div>
					))}

					<button
						type="button"
						className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-(--color-border) bg-(--color-muted) px-[18px] py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)"
						onClick={addDirector}
						style={{
							fontSize: "0.875rem",
							width: "auto",
							display: "inline-flex",
							alignItems: "center",
							gap: "8px",
						}}
					>
						<Plus size={16} />
						Add Director
					</button>
				</div>
			)}

			<div className="my-6 h-px bg-gradient-to-r from-transparent via-(--color-border) to-transparent opacity-60" />

			<div className="mb-5">
				{/* biome-ignore lint/a11y/noLabelWithoutControl: Question label for button group */}
				<Label className="mb-2 block">
					Consent for background checks on ≥10% owners
					<span aria-hidden="true" className="ml-0.5 text-(--color-error-500)">
						*
					</span>
				</Label>
				<div className="flex max-w-[400px] gap-3">
					<button
						type="button"
						className={cn(
							"flex-1 cursor-pointer rounded-sm border border-(--color-border) bg-(--color-muted) px-5 py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)",
							formData.consentForBackgroundChecks &&
								"border-(--color-primary-500) bg-white font-semibold text-(--color-primary-500) ring-(--color-primary-100) ring-[3px]",
						)}
						onClick={() => updateFormData({ consentForBackgroundChecks: true })}
					>
						Yes
					</button>
					<button
						type="button"
						className={cn(
							"flex-1 cursor-pointer rounded-sm border border-(--color-border) bg-(--color-muted) px-5 py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)",
							!formData.consentForBackgroundChecks &&
								"border-(--color-primary-500) bg-white font-semibold text-(--color-primary-500) ring-(--color-primary-100) ring-[3px]",
						)}
						onClick={() =>
							updateFormData({ consentForBackgroundChecks: false })
						}
					>
						No
					</button>
				</div>
			</div>

			<div className="mb-5">
				{/* biome-ignore lint/a11y/noLabelWithoutControl: Question label for button group */}
				<Label className="mb-2 block">
					Any owner ever convicted (excluding traffic violations)?
					<span aria-hidden="true" className="ml-0.5 text-(--color-error-500)">
						*
					</span>
				</Label>
				<div className="flex max-w-[400px] gap-3">
					<button
						type="button"
						className={cn(
							"flex-1 cursor-pointer rounded-sm border border-(--color-border) bg-(--color-muted) px-5 py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)",
							formData.anyOwnerConvicted &&
								"border-(--color-error-500) bg-white font-semibold text-(--color-error-500) ring-(--color-error-100) ring-[3px]",
						)}
						onClick={() => updateFormData({ anyOwnerConvicted: true })}
					>
						Yes
					</button>
					<button
						type="button"
						className={cn(
							"flex-1 cursor-pointer rounded-sm border border-(--color-border) bg-(--color-muted) px-5 py-2.5 font-medium text-(--color-muted-foreground) text-sm transition-all hover:bg-(--color-border) hover:text-(--color-foreground)",
							!formData.anyOwnerConvicted &&
								"border-(--color-success-500) bg-white font-semibold text-(--color-success-500) ring-(--color-success-100) ring-[3px]",
						)}
						onClick={() =>
							updateFormData({
								anyOwnerConvicted: false,
								convictionDetails: undefined,
							})
						}
					>
						No
					</button>
				</div>

				{formData.anyOwnerConvicted && (
					<div className="mt-4 space-y-1.5 rounded-(--radius-lg) border border-(--color-border) bg-(--color-muted) p-5">
						<Label htmlFor="conviction-details">
							Conviction Details
							<span
								aria-hidden="true"
								className="ml-0.5 text-(--color-error-500)"
							>
								*
							</span>
						</Label>
						<Textarea
							id="conviction-details"
							value={formData.convictionDetails || ""}
							onChange={(e) =>
								updateFormData({ convictionDetails: e.target.value })
							}
							placeholder="Please provide details about the conviction(s)"
							rows={4}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default Step3Ownership;
